import _ from 'lodash';
import React, { useEffect } from 'react';
import { appendMetadataWithIndex } from 'ml-portals-util-react';
import PropTypes from 'prop-types';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import classNames from 'classnames';

import metadata from './UnderlyingPolicyCoverageTermsComponent.metadata.json5';
import messages from './UnderlyingPolicyCoverageTermsComponent.messages';
import styles from './UnderlyingPolicyCoverageTermsComponent.module.scss';

function UnderlyingPolicyCoverageTermsComponent(props) {
    const {
        id,
        model: coverage,
        covIndex,
        onModelChange,
        onCoverageTermUpdate,
        onValidate,
        showValidationErrors,
        coverageInfo,
        termInfo,
        isExternal,
        isEdit,
        isModernProduct,
        syncCoverageTerms
    } = props;

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const isAMP = appConfig.portalCode === 'AMP';
    const translator = useTranslator();
    const termsToSync = ['PUPAutoLiabilityLiabilityLimit_ML', 'PUPAutoLiabilityUninsuredMotoristLimit_Ext', 'PUPHomeLiabilityLiabilityLimit_ML', 'PUPRecVehLiabilityLiabilityLimit_ML',
                         'PUP_ULMotoLiabilityLimit_ML', 'PUP_ULMotoLiabilityUninsuredMotoristLimit_Ext', 'PUPWatercraftLiabilityLiabilityLimit_ML'];

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const getOtherLimitValidationMessages = (term) => {

        if (!isModernProduct) {
            return [];
        }

        const value = String(term.value || '').trim();

        if (!value) {
            return [];
        }

        const isLiabilityLimit = term.name === "Other Liability Limit";
        const isUMLimit = term.name === "Other UM Limit";
        const combinedLimitRegex = /^\d{1,4}$/;
        const liabilitySplitLimitRegex = /^\d{1,4}\/\d{1,4}\/\d{1,4}$/;
        const UMSplitLimitRegex = /^\d{1,4}\/\d{1,4}$/;
        let isValid = false;

        if(isLiabilityLimit) {
            isValid = combinedLimitRegex.test(value) || liabilitySplitLimitRegex.test(value)
        }
        if(isUMLimit) {
            isValid = combinedLimitRegex.test(value) || UMSplitLimitRegex.test(value)
        }

        if (!isValid) {
            return [
                 isLiabilityLimit? translator(messages.invalidOtherLiabilityLimitFormat) : translator(messages.invalidOtherUMLimitFormat)
            ];
        }

        const limits = value.split('/').map(Number);

        if (limits.some((limit) => limit <= 0)) {
            return [
                translator(messages.otherLimitGreaterThanZero)
            ];
        }
        return [];
    };
 

    const getCoverageHelpText = (cov) => {
        const labels = coverageInfo.find((label) => label.coverageCode === cov.code_ML);
        let helpTextToReturn = '';
        if (!_.isNil(labels)) {
            helpTextToReturn = labels.helpText;
        }
        return helpTextToReturn;
    };

    const getTermHelpText = (term) => {
        const labels = termInfo.find((label) => label.termName === term.name);
        let helpTextToReturn = '';
        if (!_.isNil(labels)) {
            helpTextToReturn = labels.helpText;
        }
        return helpTextToReturn;
    };

    const getCoverageTitle = (cov) => {
        const labels = coverageInfo.find((label) => label.coverageCode === cov.code_ML);
        let titleToReturn = coverage.name;
        if (!_.isNil(labels)) {
            titleToReturn = labels.title;
        }
        return titleToReturn;
    };

    const getTermTitle = (term) => {
        const labels = termInfo.find((label) => label.termName === term.name);
        let titleToReturn = term.name;
        if (!_.isNil(labels)) {
            titleToReturn = labels.title;
        }
        return titleToReturn;
    };

    const generateOverrides = () => {
        const overrides = coverage.terms.map((term, index) => {
            return {
                [`coverageTermContainer${covIndex}${index}`]: {
                    className: isAMP ? 'col-md-6' : 'col-md-4'
                },
                [`coverageTermOptionDropdownSelect${covIndex}${index}`]: {
                    label: translator(coverage.terms.length > 1 ? getTermTitle(term) : getCoverageTitle(coverage)),
                    availableValues: term.options,
                    value: term.value,
                    disabled: !isExternal && term.value && isEdit,
                    visible: term.hasOptions,
                    onValueChange: (value) => {
                        _.set(term, 'value', value);
                        _.set(term, 'updated', true);
                        const shouldSyncCoverage = termsToSync.includes(term.codeIdentifier_ML);
                        syncCoverageTerms(shouldSyncCoverage);
                    }
                },
                [`coverageTermInput${covIndex}${index}`]: {
                    label: translator(coverage.terms.length > 1 ? getTermTitle(term) : getCoverageTitle(coverage)),
                    value: term.value,
                    readOnly: term.readOnly,
                    visible: isModernProduct && !term.hasOptions,
                    required: isModernProduct && !term.hasOptions,
                    validationMessages: getOtherLimitValidationMessages(term),
                    onValueChange: (value) => {
                        _.set(term, 'value', value);
                        _.set(term, 'updated', true);
                        onCoverageTermUpdate();
                        onModelChange();
                    }
                },
                [`coverageTermTooltipContainer${covIndex}${index}`]: {
                    className: classNames(styles.tooltipContainerOverride, isAMP ? 'col-md-6' : 'col-md-8'),
                    visible: term.hasOptions
                },
                [`coverageTermTooltip${covIndex}${index}`]: {
                    tooltipLabel: messages.coverageTermTooltipLabel,
                    tooltipContent: coverage.terms.length > 1 && !isModernProduct ? getTermHelpText(term) :
                        isModernProduct && coverage.name === 'Automobile Liability' ? getTermHelpText(term) : getCoverageHelpText(coverage),
                    tooltipContentClassName: styles.underlyingPolicyTooltip
                }
            };
        });
        return Object.assign({}, ...overrides);
    };

    const overrides = {
        [`underlyingPolicyCoverageTermsIterable${covIndex}`]: {
            data: coverage.terms
        },
        ...generateOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const updatedMetadata = appendMetadataWithIndex(metadata.componentContent, covIndex);

    return (
        <ViewModelForm
            uiProps={updatedMetadata}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrides}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

UnderlyingPolicyCoverageTermsComponent.propTypes = {
    id: PropTypes.string.isRequired,
    model: PropTypes.shape({}).isRequired,
    covIndex: PropTypes.number.isRequired,
    onModelChange: PropTypes.func.isRequired,
    onCoverageTermUpdate: PropTypes.func.isRequired,
    coverageInfo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    termInfo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    isExternal: PropTypes.bool.isRequired,
    isEdit: PropTypes.bool.isRequired,
    isModernProduct: PropTypes.bool,
    syncCoverageTerms: PropTypes.func
};

export default UnderlyingPolicyCoverageTermsComponent;
